import { getApp, getApps, initializeApp, type FirebaseOptions } from 'firebase/app'
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  signInAnonymously,
  type Auth,
  type User,
} from 'firebase/auth'
import {
  connectFirestoreEmulator,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore'

export interface FirebaseServices {
  auth: Auth
  db: Firestore
  user: User
}

export interface FirebaseEmulatorConfig {
  host: string
  authPort: number
  firestorePort: number
}

export const PRODUCTION_FIREBASE_PROJECT_ID = 'poker-pot-live'

let servicesPromise: Promise<FirebaseServices> | null = null

function positivePort(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && parsed > 0 && parsed <= 65_535 ? parsed : fallback
}

export function getFirebaseEmulatorConfig(): FirebaseEmulatorConfig | null {
  if (import.meta.env.VITE_USE_FIREBASE_EMULATORS !== 'true') return null
  return {
    host: import.meta.env.VITE_FIREBASE_EMULATOR_HOST?.trim() || '127.0.0.1',
    authPort: positivePort(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT, 9099),
    firestorePort: positivePort(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT, 8080),
  }
}

function configFromEnvironment(): FirebaseOptions | null {
  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  }

  return Object.values(config).every(Boolean) ? (config as FirebaseOptions) : null
}

async function loadFirebaseConfig(): Promise<FirebaseOptions> {
  const environmentConfig = configFromEnvironment()
  if (environmentConfig) return environmentConfig

  try {
    const response = await fetch('/__/firebase/init.json', { cache: 'no-store' })
    if (!response.ok) throw new Error('Firebase Hosting configuration is unavailable')
    return (await response.json()) as FirebaseOptions
  } catch {
    throw new Error('Firebase connection is missing · חסר חיבור ל־Firebase. Copy ‎.env.example to ‎.env.local and add the Web app details · העתיקו והוסיפו את פרטי אפליקציית ה־Web.')
  }
}

export function getFirebaseServices(): Promise<FirebaseServices> {
  if (servicesPromise) return servicesPromise

  servicesPromise = (async () => {
    const config = await loadFirebaseConfig()
    const emulator = getFirebaseEmulatorConfig()
    const app = getApps().length ? getApp() : initializeApp(config)
    const auth = getAuth(app)
    if (emulator) {
      connectAuthEmulator(auth, `http://${emulator.host}:${emulator.authPort}`, { disableWarnings: true })
    }
    await setPersistence(auth, browserLocalPersistence)
    await auth.authStateReady()
    const user = auth.currentUser ?? (await signInAnonymously(auth)).user
    const db = initializeFirestore(app, {
      localCache: emulator
        ? memoryLocalCache()
        : persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    })
    if (emulator) connectFirestoreEmulator(db, emulator.host, emulator.firestorePort)
    return { auth, db, user }
  })()

  return servicesPromise
}
